import React, { useRef, useMemo, useState, useEffect } from "react";

import ProductCard from "./ProductCard";
import { ProductGridProps } from "./types";
import Button from "../button/Button";

import styles from "./productGrid.module.css";
import useIsInViewport from "../../utils/useIsInViewport";

interface Props {
  indexOfPreviousProd: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
}

const ProductGrid = ({
  products,
  indexOfPreviousProd,
  onLoadMore,
  hasMore = true,
  isLoading = false,
}: ProductGridProps & Props): JSX.Element => {
  const loadMoreProductsRef = useRef(null);
  const [productLimit, setProductLimit] = useState<number>(14);
  const [showFromIndex, setShowFromIndex] =
    useState<number>(indexOfPreviousProd);
  const endOfProductsReached = useIsInViewport(loadMoreProductsRef);

  useMemo(() => {
    if (indexOfPreviousProd) {
      setShowFromIndex(indexOfPreviousProd);
    }
  }, [indexOfPreviousProd]);

  const showPreviousProducts = () => {
    setShowFromIndex(showFromIndex - 15);
  };

  // Handle infinite scroll - either server-side or client-side pagination
  useEffect(() => {
    if (endOfProductsReached && !isLoading) {
      if (onLoadMore && hasMore) {
        // Server-side pagination: fetch more from API
        onLoadMore();
      } else if (!onLoadMore) {
        // Client-side pagination: show more from already loaded products
        setProductLimit((prev) => prev + 15);
      }
    }
  }, [endOfProductsReached, isLoading, onLoadMore, hasMore]);

  // When using server-side pagination (onLoadMore provided), show all products
  // When using client-side pagination, apply the limit
  const useServerPagination = !!onLoadMore;

  return (
    <>
      <div className={styles.loadMoreButtonContainer}>
        {!useServerPagination && showFromIndex > 0 && (
          <Button
            text="Load previous products"
            onClick={showPreviousProducts}
          />
        )}
      </div>
      <div className={styles.container}>
        {!products ||
          (products.length < 1 && !isLoading && (
            <p className={styles.noProductsMessage}>No products found</p>
          ))}
        {useServerPagination
          ? // Server-side pagination: render all loaded products
            products &&
            products.map((product, index) => (
              <div key={product.id} className={styles.productCard}>
                <ProductCard product={product} index={index} />
              </div>
            ))
          : // Client-side pagination: apply limits
            showFromIndex
            ? products &&
              products.map((product, index) => {
                if (
                  index > showFromIndex + productLimit ||
                  index < showFromIndex
                ) {
                  return <></>;
                }
                return (
                  <div key={product.id} className={styles.productCard}>
                    <ProductCard product={product} index={index} />
                  </div>
                );
              })
            : products &&
              products.map((product, index) => {
                if (index > productLimit) {
                  return <></>;
                }
                return (
                  <div key={product.id} className={styles.productCard}>
                    <ProductCard product={product} index={index} />
                  </div>
                );
              })}
      </div>
      {isLoading && (
        <div className={styles.loadingContainer}>
          <p>Loading...</p>
        </div>
      )}
      <div ref={loadMoreProductsRef}></div>
    </>
  );
};

export default ProductGrid;
